import { useState, useMemo, useCallback, useEffect } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';
import WordFilterPanel from '@/pages/admin/cabina/moderacion/components/WordFilterPanel';
import AutoBlockConfigPanel from '@/pages/admin/cabina/moderacion/components/AutoBlockConfigPanel';
import BansTable from '@/pages/admin/cabina/moderacion/components/BansTable';
import BlockedIPsList from '@/pages/admin/cabina/moderacion/components/BlockedIPsList';
import SilencedUsersList from '@/pages/admin/cabina/moderacion/components/SilencedUsersList';
import ModHistoryLog from '@/pages/admin/cabina/moderacion/components/ModHistoryLog';
import HiddenMessagesPanel from '@/pages/admin/cabina/moderacion/components/HiddenMessagesPanel';
import ReportedMessagesPanel from '@/pages/admin/cabina/moderacion/components/ReportedMessagesPanel';
import type { BannedWord, BanEntry, BlockedIP, SilencedUser, AutoBlockConfig, AutoRule } from '@/mocks/adminModeracion';
import { bannedWords as initialWords, bans as initialBans, blockedIPs as initialIPs, silencedUsers as initialSilenced, autoBlockDefaults, autoRules as initialRules, moderationHistory as initialHistory } from '@/mocks/adminModeracion';
import { hiddenMessages as initialHidden, reportedMessages as initialReported, sensitivityDefaults } from '@/mocks/adminModeracionMessages';
import type { HiddenMessage, ReportedMessage, SensitivityConfig } from '@/mocks/adminModeracionMessages';

type FilterId = 'todos' | 'baneos' | 'ips' | 'ocultos' | 'reportados';

const filters: { id: FilterId; label: string; icon: string }[] = [
  { id: 'todos', label: 'Todos', icon: 'ri-list-check-2' },
  { id: 'baneos', label: 'Usuarios bloqueados', icon: 'ri-forbid-2-line' },
  { id: 'ips', label: 'IP bloqueadas', icon: 'ri-global-line' },
  { id: 'ocultos', label: 'Mensajes ocultos', icon: 'ri-eye-off-line' },
  { id: 'reportados', label: 'Reportados', icon: 'ri-flag-2-line' },
];

export default function ModerationTabContent() {
  const [activeFilter, setActiveFilter] = useState<FilterId>('todos');
  const [configOpen, setConfigOpen] = useState(false);
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => { setPageState('loading'); setTimeout(() => { setPageState(Math.random() < 0.07 ? 'error' : 'ready'); }, 600); }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const [words, setWords] = useState<BannedWord[]>(initialWords);
  const [bans, setBans] = useState<BanEntry[]>(initialBans);
  const [ips, setIps] = useState<BlockedIP[]>(initialIPs);
  const [silenced, setSilenced] = useState<SilencedUser[]>(initialSilenced);
  const [autoConfig, setAutoConfig] = useState<AutoBlockConfig>(autoBlockDefaults);
  const [rules, setRules] = useState<AutoRule[]>(initialRules);
  const [history] = useState(initialHistory);
  const [hiddenMsgs, setHiddenMsgs] = useState<HiddenMessage[]>(initialHidden);
  const [reportedMsgs, setReportedMsgs] = useState<ReportedMessage[]>(initialReported);
  const [sensitivity, setSensitivity] = useState<SensitivityConfig>(sensitivityDefaults);

  const stats = useMemo(() => ({
    activeBans: bans.filter((b) => b.active).length,
    activeIPs: ips.filter((ip) => ip.active).length,
    hiddenCount: hiddenMsgs.length,
    reportedPending: reportedMsgs.filter((r) => r.status === 'pendiente').length,
    totalReported: reportedMsgs.length,
    activeWords: words.filter((w) => w.active).length,
    activeSilenced: silenced.filter((s) => s.active).length,
  }), [bans, ips, hiddenMsgs, reportedMsgs, words, silenced]);

  const handleAddWord = (word: BannedWord) => setWords((prev) => [word, ...prev]);
  const handleToggleWord = (id: string) => setWords((prev) => prev.map((w) => (w.id === id ? { ...w, active: !w.active } : w)));
  const handleDeleteWord = (id: string) => {
    SwalAlert.confirm('¿Eliminar palabra?', 'La palabra será removida del filtro permanentemente.').then((result) => {
      if (result.isConfirmed) { setWords((prev) => prev.filter((w) => w.id !== id)); SwalAlert.toast('Palabra eliminada del filtro', 'success'); }
    });
  };

  const handleToggleBan = (id: string) => {
    const ban = bans.find((b) => b.id === id); if (!ban) return;
    if (ban.active) {
      SwalAlert.confirm('¿Desbloquear usuario?', `Se permitirá a ${ban.username} volver a participar en el chat.`).then((result) => {
        if (result.isConfirmed) { setBans((prev) => prev.map((b) => (b.id === id ? { ...b, active: false } : b))); SwalAlert.toast(`${ban.username} desbloqueado`, 'success'); }
      });
    } else {
      SwalAlert.confirm('¿Reactivar bloqueo?', `Se volverá a bloquear a ${ban.username}.`).then((result) => {
        if (result.isConfirmed) { setBans((prev) => prev.map((b) => (b.id === id ? { ...b, active: true, date: new Date().toISOString() } : b))); SwalAlert.toast(`${ban.username} bloqueado nuevamente`, 'success'); }
      });
    }
  };
  const handleExtendBan = (id: string) => {
    SwalAlert.confirm('¿Extender bloqueo?', 'El bloqueo se extenderá por 30 días adicionales.').then((result) => {
      if (result.isConfirmed) { setBans((prev) => prev.map((b) => b.id === id ? { ...b, duration: '30 días (extendido)', date: new Date().toISOString() } : b)); SwalAlert.toast('Bloqueo extendido 30 días', 'success'); }
    });
  };
  const handleMakePermanent = (id: string) => {
    SwalAlert.confirm('¿Convertir en permanente?', 'El bloqueo será permanente y no se podrá revertir automáticamente.').then((result) => {
      if (result.isConfirmed) { setBans((prev) => prev.map((b) => b.id === id ? { ...b, action: 'bloqueo_perm' as const, duration: 'Permanente', date: new Date().toISOString() } : b)); SwalAlert.toast('Bloqueo convertido en permanente', 'success'); }
    });
  };
  const handleToggleIP = (id: string) => {
    const ip = ips.find((x) => x.id === id); if (!ip) return;
    if (ip.active) {
      SwalAlert.confirm('¿Desbanear IP?', `Se permitirá el acceso desde ${ip.ip} nuevamente.`).then((result) => {
        if (result.isConfirmed) { setIps((prev) => prev.map((x) => (x.id === id ? { ...x, active: false } : x))); SwalAlert.toast(`IP ${ip.ip} desbaneada`, 'success'); }
      });
    } else {
      SwalAlert.confirm('¿Reactivar baneo de IP?', `Se volverá a bloquear ${ip.ip}.`).then((result) => {
        if (result.isConfirmed) { setIps((prev) => prev.map((x) => (x.id === id ? { ...x, active: true, blockedAt: new Date().toISOString() } : x))); SwalAlert.toast(`IP ${ip.ip} bloqueada nuevamente`, 'success'); }
      });
    }
  };
  const handleToggleSilenced = (id: string) => {
    const user = silenced.find((s) => s.id === id); if (!user) return;
    if (user.active) {
      SwalAlert.confirm('¿Quitar silencio?', `Se permitirá a ${user.username} volver a enviar mensajes.`).then((result) => {
        if (result.isConfirmed) { setSilenced((prev) => prev.map((s) => (s.id === id ? { ...s, active: false } : s))); SwalAlert.toast(`Silencio removido para ${user.username}`, 'success'); }
      });
    }
  };
  const handleToggleRule = (id: string) => setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r)));

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Overview cards */}
      <div className="px-4 md:px-8 pt-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Bloqueados', value: stats.activeBans, icon: 'ri-forbid-2-line', color: 'text-red-500', bg: 'bg-red-100', filter: 'baneos' as FilterId },
            { label: 'IPs bloqueadas', value: stats.activeIPs, icon: 'ri-global-line', color: 'text-rose-500', bg: 'bg-rose-100', filter: 'ips' as FilterId },
            { label: 'Mensajes ocultos', value: stats.hiddenCount, icon: 'ri-eye-off-line', color: 'text-violet-500', bg: 'bg-violet-100', filter: 'ocultos' as FilterId },
            { label: 'Reportes pendientes', value: stats.reportedPending, icon: 'ri-flag-2-line', color: 'text-red-500', bg: 'bg-red-100', filter: 'reportados' as FilterId },
            { label: 'Palabras filtro', value: stats.activeWords, icon: 'ri-file-text-line', color: 'text-secondary-500', bg: 'bg-secondary-100', filter: 'todos' as FilterId },
          ].map((s) => (
            <button key={s.label} onClick={() => { setActiveFilter(s.filter); setConfigOpen(false); }} className={`bg-background-50 border rounded-xl p-3.5 text-left cursor-pointer transition-all ${activeFilter === s.filter ? 'border-secondary-300 ring-1 ring-secondary-200' : 'border-background-200 hover:border-secondary-300'}`}>
              <div className="flex items-center gap-2 mb-1"><div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center`}><i className={`${s.icon} ${s.color} text-sm`}></i></div><span className="text-[10px] text-foreground-400 font-medium">{s.label}</span></div>
              <p className="text-lg font-bold text-foreground-900">{s.value}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Filter pills */}
      <div className="px-4 md:px-8 pt-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
            {filters.map((f) => (
              <button key={f.id} onClick={() => setActiveFilter(f.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all ${activeFilter === f.id ? 'bg-red-500 text-background-50' : 'text-foreground-500 hover:bg-background-200 hover:text-foreground-700'}`}>
                <i className={`${f.icon} text-xs`}></i>{f.label}
              </button>
            ))}
          </div>
          <button onClick={() => setConfigOpen(!configOpen)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${configOpen ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}>
            <i className="ri-settings-3-line text-xs"></i>Config
          </button>
        </div>
      </div>

      {/* Filter content */}
      <div className="px-4 md:px-8 pb-6">
        {pageState === 'loading' && <LoadingSkeleton variant="table" rows={6} cols={4} />}
        {pageState === 'error' && <ErrorState title="Error al cargar moderación" message="No pudimos obtener los datos de moderación. Intentá de nuevo." onRetry={loadData} />}
        {pageState === 'ready' && (<>
          {(activeFilter === 'todos' || activeFilter === 'baneos') && (
            <div className={activeFilter !== 'todos' ? '' : 'mb-8'}>
              {activeFilter !== 'todos' && <h4 className="text-xs font-bold text-foreground-900 mb-3">Usuarios bloqueados</h4>}
              <BansTable bans={bans} onToggleActive={handleToggleBan} onExtend={handleExtendBan} onMakePermanent={handleMakePermanent} />
            </div>
          )}
          {(activeFilter === 'todos' || activeFilter === 'ips') && (
            <div className={activeFilter !== 'todos' ? '' : 'mt-8'}>
              {activeFilter !== 'todos' && <h4 className="text-xs font-bold text-foreground-900 mb-3">IP bloqueadas</h4>}
              <BlockedIPsList ips={ips} onToggleActive={handleToggleIP} />
            </div>
          )}
          {(activeFilter === 'todos' || activeFilter === 'ocultos') && (
            <div className={activeFilter !== 'todos' ? '' : 'mt-8'}>
              {activeFilter !== 'todos' && <h4 className="text-xs font-bold text-foreground-900 mb-3">Mensajes ocultos</h4>}
              <HiddenMessagesPanel />
            </div>
          )}
          {(activeFilter === 'todos' || activeFilter === 'reportados') && (
            <div className={activeFilter !== 'todos' ? '' : 'mt-8'}>
              {activeFilter !== 'todos' && <h4 className="text-xs font-bold text-foreground-900 mb-3">Mensajes reportados</h4>}
              <ReportedMessagesPanel />
            </div>
          )}
        </>)}
      </div>

      {/* Config Section */}
      {configOpen && (
        <div className="px-4 md:px-8 pb-8">
          <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-secondary-100 flex items-center justify-center"><i className="ri-settings-3-line text-secondary-600 text-sm"></i></div>
                <div><h3 className="text-sm font-bold text-foreground-950">Configuración de moderación</h3><p className="text-[10px] text-foreground-400">Filtros, sensibilidad y reglas automáticas</p></div>
              </div>
              <button onClick={() => setConfigOpen(false)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"><i className="ri-close-line text-foreground-500"></i></button>
            </div>
            <div className="p-5 space-y-8">
              <div>
                <h4 className="text-xs font-bold text-foreground-900 mb-1 flex items-center gap-2"><i className="ri-temp-hot-line text-red-500"></i>Sensibilidad del filtro</h4>
                <p className="text-[10px] text-foreground-400 mb-4">Controlá qué tan agresivo es el sistema de moderación automática</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {[
                    { value: 'bajo' as const, label: 'Bajo', desc: 'Mínima intervención' },
                    { value: 'medio' as const, label: 'Medio', desc: 'Equilibrado' },
                    { value: 'alto' as const, label: 'Alto', desc: 'Protección estricta' },
                    { value: 'maximo' as const, label: 'Máximo', desc: 'Máxima seguridad' },
                  ].map((opt) => (
                    <button key={opt.value} onClick={() => setSensitivity((prev) => ({ ...prev, level: opt.value }))} className={`px-3 py-2.5 rounded-xl text-center cursor-pointer transition-all border ${sensitivity.level === opt.value ? 'border-secondary-300 bg-secondary-50' : 'border-background-200 bg-background-50 hover:border-background-300'}`}>
                      <p className={`text-xs font-bold ${sensitivity.level === opt.value ? 'text-secondary-600' : 'text-foreground-700'}`}>{opt.label}</p>
                      <p className="text-[9px] text-foreground-400 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-background-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2"><label className="text-[10px] font-semibold text-foreground-600">Umbral de detección</label><span className="text-xs font-bold text-foreground-900">{sensitivity.threshold}%</span></div>
                    <input type="range" min={20} max={95} value={sensitivity.threshold} onChange={(e) => setSensitivity((prev) => ({ ...prev, threshold: Number(e.target.value) }))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-red-500" style={{ background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${((sensitivity.threshold - 20) / 75) * 100}%, #e5e7eb ${((sensitivity.threshold - 20) / 75) * 100}%, #e5e7eb 100%)` }} />
                  </div>
                  <div className="bg-background-100 rounded-xl divide-y divide-background-200">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5"><i className="ri-eye-off-line text-violet-500 text-sm"></i><span className="text-[10px] font-medium text-foreground-700">Auto-ocultar mensajes</span></div>
                      <button onClick={() => setSensitivity((prev) => ({ ...prev, autoHide: !prev.autoHide }))} className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors ${sensitivity.autoHide ? 'bg-emerald-500' : 'bg-foreground-300'}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${sensitivity.autoHide ? 'left-[18px]' : 'left-0.5'}`}></span></button>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5"><i className="ri-notification-3-line text-amber-500 text-sm"></i><span className="text-[10px] font-medium text-foreground-700">Notificar al admin</span></div>
                      <button onClick={() => setSensitivity((prev) => ({ ...prev, notifyAdmin: !prev.notifyAdmin }))} className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors ${sensitivity.notifyAdmin ? 'bg-emerald-500' : 'bg-foreground-300'}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${sensitivity.notifyAdmin ? 'left-[18px]' : 'left-0.5'}`}></span></button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-background-200 pt-8"><WordFilterPanel words={words} onAdd={handleAddWord} onToggleActive={handleToggleWord} onDelete={handleDeleteWord} /></div>
              <div className="border-t border-background-200 pt-8"><AutoBlockConfigPanel config={autoConfig} onChange={setAutoConfig} rules={rules} onToggleRule={handleToggleRule} /></div>
              <div className="border-t border-background-200 pt-8"><SilencedUsersList users={silenced} onToggleActive={handleToggleSilenced} /></div>
              <div className="border-t border-background-200 pt-8"><ModHistoryLog history={history} /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}